import { supabase } from '../supabaseClient';

// Client wrapper for the admin-only /api/admin/* endpoints. These run the
// privileged auth.admin.* operations server-side (the service-role key lives
// only on the server). Every request carries the caller's Supabase access
// token so the endpoint can confirm they're an admin before acting.
//
// Replaces the old client-side `serviceSupabase.auth.admin.*` calls. Return
// shape mirrors what each call site needs; check `.ok` (and `.code` for the
// create→email_exists branch) the same way the old code checked `error`.

async function callAdminUsers(action, params = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { ok: false, status: 401, error: 'Not signed in' };
  let res;
  try {
    res = await fetch('/api/admin/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ action, ...params }),
    });
  } catch (err) {
    return { ok: false, status: 0, error: err?.message || 'Network error' };
  }
  const body = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, ...body };
}

// Ask the digest endpoint to build today's round-up and send it to YOU, now.
//
// Deliberately the same endpoint the cron calls, in a preview mode — a preview
// rendered from a second copy of the digest logic stops being a preview the
// first time the two drift, which is exactly what left every scheduled email on
// a retired design for months. The server scopes the audience to the caller, so
// this cannot fan out however it's called.
//
// Returns the endpoint's diagnostic body, which names what it found and whether
// a real run would have sent anything at all.
export async function sendDigestToMe() {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { ok: false, error: 'Not signed in' };
  let res;
  try {
    res = await fetch('/api/cron/admin-digest', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (err) {
    return { ok: false, error: err?.message || 'Network error' };
  }
  const body = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, ...body };
}

export const adminAuth = {
  // -> { ok, id } | { ok:false, code:'email_exists' | ..., error }
  createUser: ({ email, password }) => callAdminUsers('create', { email, password }),
  // -> { ok, user: { id, email } | null }
  findByEmail: (email) => callAdminUsers('find_by_email', { email }),
  // attrs: { email?, password? } -> { ok, error? }
  updateUser: (id, attrs) => callAdminUsers('update', { id, ...attrs }),
  // -> { ok, error? }
  deleteUser: (id) => callAdminUsers('delete', { id }),
  // -> { ok, user: { id, last_sign_in_at } | null }
  getUser: (id) => callAdminUsers('get', { id }),
};
